<template>
    <div class="d-flex align-items-end mb-3">
        <div class="flex-grow-1">
            <pc-alert
                shown
                v-bind:severity="isTestPageAlertSeverity"
                v-bind:message="isTestPageAlertMessage"
                class="mb-0" />
        </div>
    </div>
    <div class="d-flex align-items-end mb-3">
        <div class="flex-grow-1">
            <pc-label
                label="View with Project Number"
                bold
                for-id="ProjectNumberInput" />
            <pc-input
                id="ProjectNumberInput"
                v-model.trim="lookupProjectNumber"
                v-bind:external-native-handlers="{ keyup: onLookupProjectKeyUp }" />
        </div>
        <div>
            <pc-button
                id="UpdateInput"
                v-bind:disabled="!lookupProjectNumber"
                class="ms-2"
                v-on:click="previewTemplateWithProject">
                Load Project
            </pc-button>
        </div>
    </div>
    <div class="d-flex align-items-end mb-3">
        <div class="flex-grow-1">
            <pc-alert
                v-bind="previewAlertBinder"
                v-model:shown="previewAlertShown"
                class="mt-3 mb-0" />
        </div>
    </div>
    <hr />
    <div class="d-flex align-items-end mb-3">
        <div class="flex-grow-1">
            <pc-capsule
                v-model:shown="showProjectData"
                title="Data Fields"
                v-bind:use-background="false"
                has-body
                class="mb-3">
                <pc-loader v-bind:loading="promptStore.projectDataTableLoading">
                    <pc-table
                        key-prop="PublicId"
                        empty-text="No project data fields defined"
                        v-bind:items="promptStore.projectDataFields"
                        v-bind:columns="projectDataCols"
                        no-card-view
                        hide-search>
                    </pc-table>
                </pc-loader>
            </pc-capsule>
        </div>
    </div>
    <div class="d-flex align-items-end mb-3">
        <div class="flex-grow-1">
            <pc-select
                v-model="attachmentId"
                label="Select attachment:"
                v-bind:options="projectAttachments"
                text-prop="FileName"
                value-prop="AttachmentId"
                v-on:update:model-value="loadAttachment">
                <option value="">Select attachment...</option>
            </pc-select>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, inject, nextTick } from "vue";
    import { PAGE_APP_SYMBOL } from "_Main/injectionSymbols";
    import { ALERT_SEVERITY, ALERT_DURATION } from "Util/alerts";
    import { useEditAIPromptStore } from "AIPrompts/EditAIPrompt/editAIPromptStore";
    import { useAttachmentViewStore } from "./attachmentViewStore";

    const page = inject(PAGE_APP_SYMBOL);

    const promptStore = useEditAIPromptStore();
    const attachmentStore = useAttachmentViewStore();

    const lookupProjectNumber = ref(null);
    const showProjectData = ref(false);
    const projectId = ref(null);
    const projectAttachments = ref([]);
    const attachmentId = ref(null);

    const isTestPageAlertSeverity = ALERT_SEVERITY.warning;
    const isTestPageAlertMessage = ref(
        "This is a preview of the attachment view page. Any AI analysis generated here will not be saved."
    );

    const alertSeverity = ref(ALERT_SEVERITY.info);
    const alertDuration = ref(ALERT_DURATION.long);
    const alertMessage = ref(null);
    const previewAlertShown = ref(false);
    const previewAlertBinder = computed(() => ({
        severity: alertSeverity.value,
        duration: alertDuration.value,
        message: alertMessage.value
    }));

    const projectDataCols = computed(() => {
        const baseColumns = [
            {
                header: "PublicId",
                property: "PublicId",
                visible: false
            },
            {
                header: "Label",
                property: "Label",
                visible: true
            },
            {
                header: "Template Tag",
                property: "TemplateTagText",
                visible: true
            }
        ];

        const cols = baseColumns.map((bc) => {
            bc.orderable = false;
            bc.searchable = false;
            return bc;
        });
        return cols;
    });

    function onLookupProjectKeyUp(event) {
        if (event.key == "Enter" || event.keyCode == 13) {
            previewTemplateWithProject();
        }
    }
    async function previewTemplateWithProject() {
        page.blockUi("Loading project...");
        try {
            const result = await page.SaveTemplatePreviewAndGetProjectData({
                programId: page.ProgramId,
                projectNumber: lookupProjectNumber.value,
                previewPromptId: promptStore.previewPromptId,
                promptModel: promptStore.promptModel
            });

            if (!result.Data.Success) {
                _showAlert(result.Data.ErrorMessage, ALERT_SEVERITY.error, ALERT_DURATION.long);
            } else {
                // load attachments for project
                promptStore.previewPromptId = result.Data.PreviewPromptId;
                promptStore.projectLoaded = true;
                projectId.value = result.Data.ProjectId;
                projectAttachments.value = result.Data.ProjectAttachments;
                promptStore.projectDataFields = result.Data.ResolvedProjectDataTags;

                if (projectAttachments.value.length > 0) {
                    attachmentId.value = projectAttachments.value[0].AttachmentId;
                    await loadAttachment();
                }
                await nextTick();
            }
        } finally {
            page.unBlockUi();
        }
    }

    async function loadAttachment() {
        try {
            page.blockUi("Loading attachment...");

            if (!attachmentId.value || attachmentId.value == "") {
                attachmentStore.clearAttachment();
            } else {
                const result = await page.LoadAttachment({
                    programId: page.ProgramId,
                    projectId: projectId.value,
                    attachmentId: attachmentId.value
                });
                attachmentStore.loadStateForPreview(result.Data, promptStore.promptModel);
            }
        } finally {
            page.unBlockUi();
        }
    }

    function _showAlert(message, severity, duration) {
        alertMessage.value = message;
        alertSeverity.value = severity != null ? severity : ALERT_SEVERITY.info;
        alertDuration.value = duration != null ? duration : ALERT_DURATION.short;
        previewAlertShown.value = true;
    }
</script>
